import {
  ForensicsSearchAlarms,
  UnifiedMonitoringProduct,
} from '@hakimo-ui/hakimo/types';
import { FramePlayer } from '@hakimo-ui/hakimo/ui-elements';
import { ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import { PlayIcon } from '@heroicons/react/24/solid';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { useState } from 'react';

dayjs.extend(utc);

function AlarmCard(props: { alarm: ForensicsSearchAlarms }) {
  const { alarm } = props;
  const [isPlaying, setIsPlaying] = useState(true);
  const [frameIndex, setFrameIndex] = useState(0);

  // Remote-guarding alarms embedded via metadata frames (raw_alarm_v3, no
  // stored video) have no video_path - fall back to the same frame player used
  // for SCAN rather than rendering nothing. An alarm with neither video nor
  // frames still renders nothing; there is nothing to show.
  const isFramePlayback =
    alarm.product === UnifiedMonitoringProduct.SCAN ||
    (alarm.product === UnifiedMonitoringProduct.REMOTE_GUARDING &&
      !alarm.video_path);

  return (
    <div className="group relative overflow-hidden">
      <div className="relative aspect-video w-full overflow-hidden rounded-md bg-black">
        {/* `frames.length` rather than `frames?.length`: the latter renders a
        literal "0" when the array is empty, and it does not narrow the type. */}
        {isFramePlayback && alarm.frames && alarm.frames.length > 0 && (
          <FramePlayer
            frames={alarm.frames}
            currentFrameIndex={frameIndex}
            timeZone="UTC"
            elaborateFrames
            paused={false}
            onFrameChange={(idx) => {
              if (isPlaying) {
                setFrameIndex(idx);
              }
            }}
          />
        )}

        {alarm.product === UnifiedMonitoringProduct.REMOTE_GUARDING &&
          alarm.video_path && (
            <video
              src={alarm.video_path}
              controls
              autoPlay
              muted
              loop
              onPlay={() => setIsPlaying(true)}
              onPause={() => setIsPlaying(false)}
              className="h-full w-full object-cover"
              poster=""
              preload="metadata"
            />
          )}

        <div
          className={`absolute inset-0 z-10
            ${isFramePlayback ? 'cursor-pointer' : 'pointer-events-none'}`}
          onClick={() => {
            if (isFramePlayback) {
              setIsPlaying((p) => !p);
            }
          }}
        >
          {!isPlaying && (
            <div className="flex h-full w-full items-center justify-center opacity-0 transition-opacity duration-200 group-hover:opacity-100">
              <div className="rounded-full bg-white/20 p-4 backdrop-blur-sm">
                <PlayIcon className="h-8 w-8 text-white" />
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="flex items-start justify-between">
        <div className="mt-2 text-sm leading-tight text-gray-800 dark:text-white">
          {alarm.camera_name || '-'}
          <br />
          <span className="text-onlight-text-3 dark:text-ondark-text-2 text-xs">
            {alarm.location_name || '-'}
          </span>
        </div>

        <div className="mt-2 flex items-center gap-2 text-right">
          <span className="text-onlight-text-3 dark:text-ondark-text-2 mt-0.5 text-xs">
            {dayjs(alarm.alarm_timestamp_utc).format('YYYY/MM/DD, HH:mm:ss')}
          </span>
          <a
            href={
              alarm.product === UnifiedMonitoringProduct.SCAN
                ? `/alarms/${alarm.event_id}?product=scan`
                : `/alarms/${alarm.alarm_id}?partitionKey=${dayjs(
                    alarm.alarm_timestamp_utc
                  ).format('YYYYMM')}`
            }
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
          >
            <ArrowTopRightOnSquareIcon className="text-onlight-text-3 dark:text-ondark-text-2 h-4 w-4" />
          </a>
        </div>
      </div>
    </div>
  );
}

export default AlarmCard;
